import React, { useEffect, useState } from "react";
import { Modal, Input, Button, message, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CloseOutlined,
  CopyOutlined,
  ShareAltOutlined,
  ExclamationCircleOutlined,
  LinkOutlined,
  LockOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  getShareInfo,
  createShare,
  toggleShare,
  regenerateShare,
  type ShareInfo,
} from "./balancedScorecard.service";

type Props = {
  open: boolean;
  scorecardId: string;
  onClose: () => void;
};

const PublicBalancedScorecardHistoryShareModal: React.FC<Props> = ({
  open,
  scorecardId,
  onClose,
}) => {
  const { t } = useTranslation("dashboardModals");
  const navigate = useNavigate();
  const [shareInfo, setShareInfo] = useState<ShareInfo | null>(null);
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !scorecardId) return;

    setShareInfo(null);
    setLoadError(null);

    (async () => {
      setLoadingInfo(true);
      try {
        const info = await getShareInfo(scorecardId);
        setShareInfo(info);
      } catch (err: any) {
        if (err?.response?.status === 404) {
          try {
            const created = await createShare(scorecardId);
            setShareInfo(created);
          } catch (createErr: any) {
            if (createErr?.response?.status === 402) {
              setLoadError(
                createErr?.response?.data?.detail ??
                  t("shareModal.sharingRequiresPaidPlan"),
              );
            } else {
              setLoadError(t("shareModal.createShareLinkError"));
              message.error(t("shareModal.createShareLinkError"));
            }
          }
        } else {
          setLoadError(t("shareModal.loadShareInfoError"));
          message.error(t("shareModal.loadShareInfoError"));
        }
      } finally {
        setLoadingInfo(false);
      }
    })();
  }, [open, scorecardId]);

  const frontendShareUrl = shareInfo?.share_token
    ? `${window.location.origin}/share/bsc/${shareInfo.share_token}`
    : "";

  const handleCopy = () => {
    if (!frontendShareUrl) return;
    navigator.clipboard.writeText(frontendShareUrl).then(() => {
      message.success(t("shareModal.copiedToClipboard"));
    });
  };

  const handleDisableConfirm = async () => {
    if (!scorecardId) return;
    setSaving(true);
    try {
      const updated = await toggleShare(scorecardId, false);
      setShareInfo(updated);
      setConfirmOpen(false);
    } catch {
      message.error(t("shareModal.disableAccessError"));
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateNew = async () => {
    if (!scorecardId) return;
    setSaving(true);
    try {
      const updated = await regenerateShare(scorecardId);
      setShareInfo(updated);
    } catch {
      message.error(t("shareModal.generateNewLinkError"));
    } finally {
      setSaving(false);
    }
  };

  const accessEnabled = shareInfo?.is_active ?? false;

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        width={520}
        centered
        closeIcon={<CloseOutlined style={{ fontSize: 18, color: "#888" }} />}
      >
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              background: "#F3EEFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <ShareAltOutlined style={{ fontSize: 22, color: "#4A1C96" }} />
          </div>
          <h2
            style={{
              margin: "0 0 6px",
              color: "#4A1C96",
              fontSize: 20,
              fontWeight: 700,
            }}
          >
            {t("shareModal.title")}
          </h2>
          <p style={{ margin: 0, color: "#888", fontSize: 14 }}>
            {t("shareModal.subtitle")}
          </p>
        </div>

        <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: 24 }}>
          {loadingInfo ? (
            <div style={{ textAlign: "center", padding: "24px 0" }}>
              <Spin />
            </div>
          ) : loadError ? (
            <div style={{ textAlign: "center", padding: "12px 0 8px" }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  background: "#f5f5f5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 16px",
                }}
              >
                <LockOutlined style={{ fontSize: 22, color: "#aaa" }} />
              </div>
              <p
                style={{
                  fontWeight: 700,
                  fontSize: 15,
                  color: "#333",
                  margin: "0 0 6px",
                }}
              >
                {t("shareModal.createLinkErrorTitle")}
              </p>
              <p style={{ fontSize: 13, color: "#888", margin: "0 0 24px" }}>
                {loadError}
              </p>
              <Button
                onClick={() => navigate("/subscription-plans")}
                style={{
                  borderColor: "#4A1C96",
                  color: "#4A1C96",
                  background: "#F3EEFF",
                  fontWeight: 600,
                }}
              >
                {t("shareModal.seePlans")}
              </Button>
            </div>
          ) : accessEnabled ? (
            <>
              <div style={{ marginBottom: 20 }}>
                <p
                  style={{
                    margin: "0 0 8px",
                    fontWeight: 600,
                    fontSize: 13,
                    color: "#444",
                  }}
                >
                  {t("shareModal.shareableLinkLabel")}
                </p>
                <div style={{ display: "flex", gap: 8 }}>
                  <Input
                    value={frontendShareUrl}
                    readOnly
                    style={{
                      flex: 1,
                      color: "#555",
                      background: "#fafafa",
                      fontSize: 13,
                    }}
                  />
                  <Button
                    icon={<CopyOutlined />}
                    onClick={handleCopy}
                    style={{
                      borderColor: "#4A1C96",
                      color: "#4A1C96",
                      flexShrink: 0,
                    }}
                  >
                    {t("shareModal.copyLink")}
                  </Button>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  paddingTop: 16,
                  borderTop: "1px solid #f0f0f0",
                }}
              >
                <div>
                  <p
                    style={{
                      margin: 0,
                      fontWeight: 600,
                      fontSize: 14,
                      color: "#333",
                    }}
                  >
                    {t("shareModal.accessLabel")}
                  </p>
                  <p style={{ margin: 0, fontSize: 13, color: "#888" }}>
                    {t("shareModal.anyoneWithLink")}
                  </p>
                </div>
                <Button
                  onClick={() => setConfirmOpen(true)}
                  loading={saving}
                  style={{
                    borderColor: "#4A1C96",
                    color: "#4A1C96",
                    background: "#F3EEFF",
                    fontWeight: 600,
                  }}
                >
                  {t("shareModal.disableAccess")}
                </Button>
              </div>
            </>
          ) : (
            <div style={{ textAlign: "center", padding: "12px 0 8px" }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  background: "#f5f5f5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 16px",
                }}
              >
                <LinkOutlined style={{ fontSize: 22, color: "#aaa" }} />
              </div>
              <p
                style={{
                  fontWeight: 700,
                  fontSize: 15,
                  color: "#333",
                  margin: "0 0 6px",
                }}
              >
                {t("shareModal.linkAccessDisabledTitle")}
              </p>
              <p style={{ fontSize: 13, color: "#888", margin: "0 0 24px" }}>
                {t("shareModal.linkDisabledDesc")}
              </p>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  paddingTop: 16,
                  borderTop: "1px solid #f0f0f0",
                }}
              >
                <p style={{ margin: 0, fontSize: 13, color: "#888" }}>
                  {t("shareModal.changedMindLabel")}
                </p>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleGenerateNew}
                  loading={saving}
                  style={{
                    borderColor: "#4A1C96",
                    color: "#4A1C96",
                    background: "#F3EEFF",
                    fontWeight: 600,
                  }}
                >
                  {t("shareModal.generateNew")}
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        footer={null}
        width={420}
        centered
        closeIcon={<CloseOutlined style={{ fontSize: 16, color: "#888" }} />}
      >
        <div style={{ textAlign: "center", padding: "8px 0 4px" }}>
          <ExclamationCircleOutlined
            style={{ fontSize: 28, color: "#4A1C96", marginBottom: 12 }}
          />
          <h3
            style={{
              margin: "0 0 10px",
              color: "#4A1C96",
              fontSize: 18,
              fontWeight: 700,
            }}
          >
            {t("shareModal.confirmDisableTitle")}
          </h3>
          <p style={{ color: "#666", fontSize: 14, margin: "0 0 28px" }}>
            {t("shareModal.confirmDisableDesc")}
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
            <Button
              onClick={() => setConfirmOpen(false)}
              style={{ minWidth: 90 }}
            >
              {t("shareModal.cancel")}
            </Button>
            <Button
              loading={saving}
              onClick={handleDisableConfirm}
              style={{
                minWidth: 120,
                background: "#4A1C96",
                color: "#fff",
                borderColor: "#4A1C96",
                fontWeight: 600,
              }}
            >
              {t("shareModal.disableAccess")}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default PublicBalancedScorecardHistoryShareModal;
